import {
  differenceInDays,
  differenceInMinutes,
  differenceInSeconds,
  endOfDay,
  differenceInHours,
  format,
  getUnixTime,
  getYear,
  intervalToDuration,
  isSameDay,
  startOfDay,
  addDays,
  parseISO,
} from 'date-fns';
import { TZDate } from '@date-fns/tz';

import { pluralize } from './stringUtils';

type DateInput = Date | string | number | null;

export const countriesUsing12HourFormat = [
  'Egypt',
  'Bangladesh',
  'India',
  'Jordan',
  'Pakistan',
  'Philippines',
  'Malaysia',
  'Saudi Arabia',
  'El Salvador',
  'Honduras',
  'Nicaragua',
  'Ireland',
  'Canada',
  'Mexico',
  'United States',
  'Australia',
  'New Zealand',
  'Colombia',
];

const formatDate = (date: DateInput, dateFormat: string) => {
  if (date === null) return 'Invalid date';
  return format(new Date(date), dateFormat);
};
export const dateMonthYear = (date: DateInput) => formatDate(date, 'dd.MM.yy');
export const dateWithTimestamp = (date: DateInput) => formatDate(date, 'HH:mm - dd MM yyyy');
export const dateWithDayName = (date: DateInput) => formatDate(date, 'EE dd MMM yyyy');
export const dateWithMonthName12HourFormat = (date: DateInput) => formatDate(date, 'MMMM do yyyy h.mm a');
export const dateWithMonthName24HourFormat = (date: DateInput) => formatDate(date, 'MMMM do yyyy HH.mm');
export const norwegianDateWithTimestamp = (date: DateInput) => formatDate(date, 'dd.MM.yy HH:mm');
export const asShortDate = (date: DateInput) => formatDate(date, 'dd MMM yy');
export const asShortDateWithoutYear = (date: DateInput) => formatDate(date, 'dd MMM');

export const yearMonthDate = (date: DateInput) => formatDate(date, 'yyyy.MM.dd');
export const asDayMonthNameYear = (date: DateInput) => formatDate(date, 'd MMMM yyyy');
export const asDayMonthName = (date: DateInput) => formatDate(date, 'd MMMM');
export const asMonthNameYear = (date: DateInput) => formatDate(date, 'MMMM yyyy');
export const asMonthYear = (date: DateInput) => formatDate(date, 'MMM yyyy');
export const asTime = (date: DateInput) => formatDate(date, 'HH:mm');
export const asDayShortMonth = (date: DateInput) => formatDate(date, 'd MMM');
export const asDayShortMonthYear = (date: DateInput) => formatDate(date, 'd MMM yyyy');

/**
 * Timestamp formatting for message/conversation lists, where the column is narrow and the
 * reader scans for recency rather than an exact age. `timeSince` is the wrong shape here — it
 * returns '2 hours ago' / '5 December'.
 *   today      -> '10:30'
 *   yesterday  -> 'Yesterday'
 *   this year  -> '5 Dec'
 *   older      -> '31 Oct 2024'
 */
export const asInboxTimestamp = (date: DateInput) => {
  if (!date) return '';

  const target = new Date(date);
  if (isNaN(target.getTime())) return '';

  const now = new Date();

  if (isSameDay(now, target)) return asTime(target);
  if (isSameDay(addDays(now, -1), target)) return 'Yesterday';
  if (getYear(now) === getYear(target)) return asDayShortMonth(target);

  return asDayShortMonthYear(target);
};
export const currentYear = () => format(new Date(), 'yyyy');
export const iso8601DateString = (date: Date) => format(date, 'yyyy-MM-dd');

// This is handy because the datepicker uses only the date part but includes the time zone, which has a tendency to skew dates 🫠
export const asIsoDateOnly = (date?: Date | null) => (date ? new Date(iso8601DateString(date)).toISOString() : date);
export const asUnixMillis = (t: Date) => getUnixTime(t) * 1000;

export const formatEventDate = (from: Date, to?: Date, location?: string): string => {
  const uses12HourFormat = location && countriesUsing12HourFormat.includes(location);

  const fromDateFormatted = uses12HourFormat
    ? dateWithMonthName12HourFormat(from)
    : dateWithMonthName24HourFormat(from);
  if (!to) return fromDateFormatted;

  const toDateFormatted = uses12HourFormat ? dateWithMonthName12HourFormat(to) : dateWithMonthName24HourFormat(to);

  const sameDay = from.toDateString() === to.toDateString();
  return sameDay
    ? `${fromDateFormatted} - ${format(to, uses12HourFormat ? 'h.mm a' : 'HH.mm')}`
    : `${fromDateFormatted} - ${toDateFormatted}`;
};

export function getDateRange(endDate: Date, days: number) {
  return [startOfDay(addDays(endDate, -(days - 1))), endOfDay(endDate)];
}

export const getDifferenceInDays = (start: Date, end: Date) => {
  const endOfStart = startOfDay(start);
  const endOfEnd = endOfDay(end);
  return intervalToDuration({ start: endOfStart, end: endOfEnd }).days || 0;
};

export const yearsMonthsDaysSince = (date: DateString, now?: DateString) => {
  const today = startOfDay(now ? new Date(now) : new Date());
  const start = startOfDay(new Date(date));

  const duration = intervalToDuration({ start: start, end: today });

  const dateTexts = [];
  if (duration.years) {
    dateTexts.push(`${duration.years} ${pluralize(duration.years, 'year', 'years')}`);
  }
  if (duration.months) {
    dateTexts.push(`${duration.months} ${pluralize(duration.months, 'month', 'months')}`);
  }
  if (duration.days) {
    dateTexts.push(`${duration.days} ${pluralize(duration.days, 'day', 'days')}`);
  }

  // We only pick the largest two blocks of time for display, so that it is easy to read
  // Meaning that if the time since is 2 years, 3 months and 4 days, we only show "2 years and 3 months"
  return dateTexts.slice(0, 2).join(' and ');
};

export const minimumDaysForDateDisplay = 32;

export function daysAgo(isoDate: string | null): string {
  if (!isoDate) {
    return 'Never';
  }
  return `${differenceInDays(new Date(), parseISO(isoDate))} days ago`;
}

export function timeSince(date: Date | string) {
  if (!date) return '';

  const startDate = new Date(date);

  if (isNaN(startDate.getTime())) return '';

  const now = new Date();
  const days = differenceInDays(startOfDay(now), startOfDay(startDate));
  const hours = differenceInHours(now, startDate);
  const minutes = differenceInMinutes(now, startDate);
  const seconds = differenceInSeconds(now, startDate);

  const postfix = ' ago';

  if (seconds < 60) return 'Just now';
  if (minutes < 2) return minutes + ' minute' + postfix;
  if (minutes < 60) return minutes + ' minutes' + postfix;
  if (hours < 2) return hours + ' hour' + postfix;
  if (hours < 24) return hours + ' hours' + postfix;
  if (days < 2) return 'Yesterday';
  if (days < minimumDaysForDateDisplay) return days + ' days' + postfix;
  if (getYear(now) === getYear(startDate)) return asDayMonthName(date);
  return asDayMonthNameYear(date);
}

export function sortDates(a: DateString, b: DateString): number {
  return new Date(a).getTime() - new Date(b).getTime();
}

// Function to get full timezone name
export function getTimezoneDisplayName(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long',
    });
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : timezone;
  } catch {
    return timezone;
  }
}

// Create a timezone-aware date from DatePicker selection
// When user selects a date/time, treat it as local time in the specified timezone
export function createTimezoneAwareDate(localDate: Date | null, timezone: string): Date | null {
  if (!localDate) return null;

  try {
    const year = localDate.getFullYear();
    const month = localDate.getMonth();
    const day = localDate.getDate();
    const hour = localDate.getHours();
    const minute = localDate.getMinutes();
    const second = localDate.getSeconds();

    // Create a TZDate with these components in the configured timezone
    const tzDate = new TZDate(year, month, day, hour, minute, second, 0, timezone);
    return new Date(tzDate.getTime());
  } catch (error) {
    console.warn('Error creating timezone-aware date:', error);
    return localDate;
  }
}

// Convert a UTC date to display properly in the DatePicker for the specified timezone
// The DatePicker needs to show the local time as it appears in the specified timezone
export function convertUtcToDisplayDate(utcDate: Date | null, timezone: string): Date | null {
  if (!utcDate) return null;

  try {
    // Get the local time components as they appear in the specified timezone
    // Using 'en-CA' locale because it provides ISO 8601 format (YYYY-MM-DD) with consistent separators
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(utcDate);
    const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
    const month = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1; // Month is 0-indexed
    const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    const second = parseInt(parts.find(p => p.type === 'second')?.value || '0');

    // Create a date object that will display these components in the DatePicker
    return new Date(year, month, day, hour, minute, second);
  } catch (error) {
    console.warn('Error converting UTC to display date:', error);
    return utcDate;
  }
}

// Utility function to maintain the same wall clock time when changing timezones
// For example: if it's 2:00 PM in New York, keep it as 2:00 PM when switching to Los Angeles
export function convertDateToTimezone(date: Date | null, fromTimezone: string, toTimezone: string): Date | null {
  if (!date || fromTimezone === toTimezone) return date;

  try {
    // Extract the local time components as they appear in the source timezone
    // Using 'en-CA' locale because it provides ISO 8601 format (YYYY-MM-DD) with consistent separators
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: fromTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
    const month = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1; // Month is 0-indexed
    const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    const second = parseInt(parts.find(p => p.type === 'second')?.value || '0');

    // Use date-fns TZDate to create a date with these components in the target timezone
    const tzDate = new TZDate(year, month, day, hour, minute, second, 0, toTimezone);

    // Convert back to a regular Date object
    return new Date(tzDate.getTime());
  } catch (error) {
    console.warn('Error converting date between timezones:', error);
    return date;
  }
}

// Format deadline with time in user's timezone for display
export function formatDeadlineWithTimezone(deadline: DateString): string {
  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userTimezoneName = getTimezoneDisplayName(userTimezone);

  // Format the deadline in user's local timezone
  const formattedDate = asDayMonthNameYear(deadline);
  const formattedTime = asTime(deadline);

  const dateWithTime = `${formattedDate} at ${formattedTime}`;

  // Always show user's timezone for clarity
  return `${dateWithTime} (${userTimezoneName})`;
}
